import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage21() {
  const contentRef = useRef(null);
    const links = {
    prev: "/islamic-trade-page-20", 
    index: "/index",                 
    next: "/islamic-trade-page-22", 
  };
    const titleText = "Lending for Use Without Gain (Ariyah)";
    const questions = [
      {
        text: "1. In Islamic law, Aariyah refers to:",
        options: [
          {
            value: "A",
            label:
              "A. Lending money for interest.",
          },
          {
            value: "B",
            label:
              "B. Renting a property for profit.",
          },
          {
            value: "C",
            label:
              "C.  Lending an item for use without expecting compensation.",
          },
          {
            value: "D",
            label:
              "D. Selling goods on credit.",
          },
        ],
        correct: "C",
      },
      {
        text: "2. Which of the following is not a valid condition for Aariyah in Islamic jurisprudence?",
        options: [
          { value: "A", label: "A. The lender must own or be authorized to lend the item" },
          { value: "B", label: "B. The borrower must return the item after using it" },
          { value: "C", label: "C. The lender can reclaim the item at any time, even before the agreed use ends" },
          { value: "D", label: "D. The item must be permissible for use under Shariahs" },
        ],
        correct: "C",
      },
      {
        text: "3. If a borrower loses a lent item without negligence or misuse, what is the ruling in Islam?",
        options: [
          { value: "A", label: "A. The borrower must always compensate the lender" },
          { value: "B", label: "B. The lender is entitled to demand compensation" },
          { value: "C", label: "C. The borrower is not liable to compensate" },
          { value: "D", label: "D. The item becomes the property of the borrower" },
        ],
        correct: "C",
      },
      {
        text: "4. Which hadith principle supports the idea that the lender should not take back the item before the borrower has completed the intended use?",
        options: [
          { value: "A", label: "A. “The borrower must return the item after one week.”" },
          { value: "B", label: "B. “There should be neither harm nor reciprocating harm.”" },
          { value: "C", label: "C. “The lender has full control over the item at all times.”" },
          { value: "D", label: "D. “Profits must be divided equally.”" },
        ],
        correct: "B",
      },
    ];
    // State to track selected answers for all questions
    const [answers, setAnswers] = useState({});
  
    // Function to update an answer
    const handleAnswerChange = (questionIndex, selectedValue) => {
      setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
    };
  
    // Compute score
    const score = questions.reduce((total, q, index) => {
      return total + (answers[index] === q.correct ? 1 : 0);
    }, 0);
  

  return (
    
    <div className="container">
       <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={21}
        timerMinutes={60}
        currentLevel={1}
      />
      
      <p className="content" ref={contentRef}>
        Welcome to <b>Day 21</b>! Today, we explore the noble and generous
        practice of Ariyah i.e lending for use without expecting any
        compensation. Rooted in the Islamic values of trust, cooperation, and
        mutual support, Ariyah encourages believers to help one another by
        temporarily sharing useful items while respecting ownership rights.
        Whether it's lending a book, a tool, or even a vehicle, this selfless
        act strengthens community ties and fosters goodwill. In this lesson, we
        will examine the conditions, responsibilities, and ethical guidelines
        surrounding Ariyah, as well as how it was practiced by the Prophet
        Muhammad (peace be upon him) and emphasized in Islamic jurisprudence.
        <br /> In Islamic jurisprudence, Aariyah represents a noble act of
        lending an item for use without expecting compensation. Rooted in
        principles of generosity, trust, and mutual benefit, this practice
        emphasizes the ethical importance of aiding others while respecting
        ownership rights. A person offering Aariyah must either own the item or
        be authorized to lend it, ensuring that the rights of others are not
        infringed upon. For example, a guardian of an orphan is prohibited from
        engaging in Aariyah with the orphan’s belongings unless it directly
        benefits the orphan. This reflects Islam’s commitment to safeguarding
        the vulnerable and preventing misuse of entrusted property.
        <br />
        <br /> The concept of Aariyah aligns with the Qur’anic directive to aid
        one another in righteousness and piety: <br />
        “Help one another in acts of righteousness and piety, and do not assist
        each other in sin and transgression.”
        <i>
          <a href="https://quran.com/5/2" target="blank">
            (Surah Al Ma'idah 5:2)
          </a>
        </i>{" "}
        <br /> The practice was also exemplified by the Prophet Muhammad (peace
        be upon him), who lent his horse to Abu Talha for use. Abu Talha
        reported: “The Prophet (peace be upon him) borrowed a horse belonging to
        me called Mandub, and he rode it. When he returned, he said: ‘I found
        your horse to be very fast.’”
        <i>
          <a href="https://sunnah.com/bukhari:2627" target="blank">
            {" "}
            (Sahih al-Bukhari, Hadith 2629)
          </a>
        </i>
        <br />
        <br />
        The beauty of Aariyah lies in its accommodation of customary usage. When
        an item like a pen, a car, or a household tool is lent, normal wear and
        tear, such as ink depletion or fuel consumption, is overlooked. This
        leniency fosters cooperation and goodwill among community members,
        encouraging resource sharing without undue burden or contention. By
        upholding this practice, Islam not only nurtures bonds of trust but also
        promotes a balanced view of material possessions as tools for collective
        benefit, not mere individual gain.
        <br />
        <br />
        <b>Lending Permissible Items: Conditions and Limits</b>
        <br />
        <br />
        In Islam, lending an item for use is a virtuous act, but it must adhere
        to certain conditions. The item being lent must be permissible to use;
        for instance, it is not allowed to lend a silk garment to a man to wear,
        as it is prohibited for men in Islamic law. Similarly, the borrower must
        use the item responsibly, avoiding recklessness, excess, or habitual
        dependence. Their usage should align with customary norms, ensuring that
        the item is neither misused nor degraded unnecessarily. Lending, while
        encouraged, remains a balanced act that requires both parties to observe
        proper ethical conduct.
        <br />
        <br />
        <b>When Lending Becomes Recommended or Obligatory</b>
        <br />
        <br />
        Lending can sometimes be more than a recommended act it can become
        obligatory. For example, if a person requests a blanket during severe
        cold, and the lender has a spare, it becomes mandatory to provide it to
        save the individual from potential harm. Similarly, lending books to
        someone seeking to learn obligatory religious knowledge falls under the
        category of a required act, as it facilitates the fulfillment of
        essential Islamic duties.
        <br />
        As the Qur’an states: “And do good; indeed, Allah loves the doers of
        good.”
        <i>
          <a href="https://quran.com/2/195" target="blank">
            (Surah Al-Baqarah, 2:195)
          </a>
        </i>
        <br /> However, if there is reasonable concern that the borrower will
        misuse or damage the item, such as staining a book or scribbling on its
        pages, the lender is permitted to refuse. This balance reflects Islam’s
        emphasis on both generosity and the preservation of personal property.
        <br />
        <br />
        <b>Respecting the Borrower’s Purpose and Duration</b>
        <br />
        <br />
        In Islamic ethics, it is impermissible for the lender to reclaim an item
        before the borrower has fulfilled the purpose for which it was lent. For
        instance, if someone borrows a car for a round trip, the lender cannot
        demand its return midway, as doing so would harm the borrower and
        disrupt their need. Islam places a strong emphasis on preventing harm,
        as reflected in the prophetic saying: “There should be neither harming
        nor reciprocating harm.”{" "}
        <i>
          <a href="https://sunnah.com/ibnmajah:2341" target="blank">
            (Sunan Ibn Majah, Hadith 2341)
          </a>
        </i>
        <br />
        This principle ensures that lending remains an act of generosity and
        support, free from undue interference or inconvenience to the borrower.
        <br />
        Additionally, if an item is lent for a specified duration, it is not
        permissible for the lender to ask for its return before the agreed time
        lapses. This is because the borrower is entitled to the full use of the
        item within the agreed terms, and prematurely reclaiming it would
        violate that agreement. Such guidelines reflect the fairness and trust
        Islam seeks to cultivate in interpersonal dealings. By respecting these
        rules, both parties uphold the spirit of cooperation and goodwill, which
        are foundational to Islamic social ethics.
        <br />
        Islam emphasizes honoring agreements and commitments, as reflected in
        the prophetic saying: "And the Muslims will be held to their conditions,
        except the conditions that make the lawful unlawful, or the unlawful
        lawful.{" "}
        <i>
          <a href="https://sunnah.com/tirmidhi:1352" target="blank">
            {" "}
            (Sunan Abu Dawood, Hadith 3594)
          </a>
        </i>{" "}
        <br />
        This principle ensures that lending remains an act of generosity and
        support, while fostering trust and mutual respect between the lender and
        the borrower.
        <br />
        <br />
        <b>Responsibility for Lost or Destroyed Lent Items</b>
        <br />
        <br />
        In Islamic law, if a lent object is lost or destroyed without negligence
        or misuse, the borrower is not required to compensate the lender. This
        is because the act of lending is considered a voluntary trust, and the
        destruction of a trust, when caused by circumstances beyond the
        borrower’s control, carries no liability. This ruling emphasizes the
        principle of trust and mutual goodwill in lending agreements, as the
        lender willingly entrusts their property to the borrower. <br />
        As mentioned in the Qur'an:
        <br />
        “Indeed, Allah commands you to render trusts to whom they are due and
        when you judge between people to judge with justice…”{" "}
        <i>
          <a href="https://quran.com/4/58" target="blank">
            (Surah An-Nisa 4:58)
          </a>
        </i>
        <br />
        This verse highlights the importance of returning trusts, which extends
        to lending situations. If the trust is lost or destroyed without fault,
        the borrower is not held liable, as long as there was no breach of trust
        or negligence. <br />
        However, scholars outline exceptions where compensation becomes
        mandatory. If the borrower is careless, misuses, or abuses the item,
        they are held responsible for its loss or damage. <br />
        Additionally, if the lender and borrower mutually agree beforehand that
        compensation will be required in the event of loss or destruction, the
        borrower is obligated to fulfill that condition. In such cases, the
        borrower must compensate the lender with an equivalent item, if
        available, or with the monetary value of the item as it was on the day
        it was lost or destroyed.
        <br />
        This balance between trust and accountability ensures fairness and
        protects both parties in lending transactions.
        <br />
        <br />
        <b>Obligations of the Borrower in Returning Lent Items</b>
        <br />
        <br />
        It is obligatory for the borrower to return the lent item under certain
        circumstances, as outlined by most Islamic scholars. Firstly, if the
        time specified for lending has expired, the borrower must return the
        item. Secondly, if the purpose of the lending has been fulfilled, such
        as completing the task for which the item was borrowed, it should be
        returned. Additionally, if the borrower fears that the item may be
        stolen or damaged while in their possession, they are required to return
        it to ensure its safety. Lastly, if the borrower is traveling and will
        not be able to return the item before the term expires, they must return
        it as soon as they are able or make suitable arrangements to do so.
        These conditions highlight the importance of maintaining trust (Amanah)
        and ensuring the proper handling and return of borrowed property in
        Islamic law.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        In today's lesson, we explored the Islamic concept of Aariyah i.e
        lending items for use without expecting compensation. This noble act
        reflects generosity, mutual trust, and ethical responsibility. We
        learned that Aariyah is only valid if the lender owns or is authorized
        to lend the item, and the borrower must use it within customary limits.
        Islam emphasizes honoring lending agreements, prohibiting early
        retrieval of items unless justified. We also discussed the borrower's
        obligation to return items upon contract completion and the conditions
        under which they may be held liable for loss or damage. Ultimately,
        Aariyah reinforces community cooperation, trust, and the balanced
        protection of both parties’ rights.
        <br />
        <br />
        <b>Coming Up Next</b>
        <br />
        <br />
        Tomorrow, we explore the principle of Shuf’ah i.e the Islamic right of
        pre-emption in property transactions. This unique legal provision
        protects the rights of co-owners and neighbors by giving them the first
        opportunity to purchase a shared or adjacent property before it is sold
        to outsiders. We'll examine how this right preserves community harmony,
        the conditions under which it applies, and the wisdom behind its
        limitation to immovable property like land. Join us as we study how
        Shuf’ah upholds fairness and social cohesion in property dealings under
        Islamic law.
      </p>
      <h2>Test your understanding</h2>
       {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}
      
     

     
       <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage21;
